import { SyntheticEvent } from "react";

/**
 * True when a React synthetic event was raised inside a portalled descendant —
 * Radix `Dialog`/`Popover`/`DropdownMenu` content, which renders into
 * `document.body` rather than under the component that declared it.
 *
 * React bubbles synthetic events through the **React tree**, not the DOM tree,
 * so a click inside a portal still reaches the handlers of every React ancestor
 * even though the clicked node lives outside their DOM subtree. Click-to-select
 * surfaces (a board card, a list row) must ignore those events: a click on a
 * dialog's Save button is a click on the dialog, not on the card behind it.
 *
 * Strictly this asks "is the target outside my DOM subtree?", which is also true
 * for a node detached before the handler ran. That is the intended reading — in
 * both cases the event did not come from this element's own content.
 *
 * @see WEBV3-161 — card action dialogs were opening the RO side panel.
 */
export const isEventFromPortal = <T extends Element>(
  event: SyntheticEvent<T>
): boolean => !event.currentTarget.contains(event.target as Node);
